// Voice Clone Service for ElevenLabs voice cloning + RVM
import { showToast } from "../utils/show-toast";
import { normalizeApiError } from "../utils/apiResponseHandler";
import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";

export type VoiceCloneStatus = 'processing' | 'ready' | 'failed';

export interface VoiceClone {
  id: string;
  userId: string;
  agencyId?: string;
  voiceId: string;
  name: string;
  sampleAudioUrl?: string;
  sampleDuration?: number;
  status: VoiceCloneStatus;
  isDefault: boolean;
  isActive: boolean;
  previewAudioUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp?: string;
}

/**
 * Unwrap an API payload, tolerating the DOUBLE envelope.
 *
 * The backend's global `TransformInterceptor` (main.ts) only treats a response
 * as "already standardized" when it contains statusCode + message + data +
 * **timestamp**. Controllers that return `{ statusCode, message, data }` without
 * a timestamp — which is most of them, including voice-clone — fail that check
 * and get wrapped a SECOND time:
 *
 *   { statusCode, message, data: { statusCode, message, data: <payload> } }
 *                                 └──────────── apiResponse.data ──────────┘
 *
 * So `apiResponse.data` is the inner ENVELOPE, not the payload.
 *
 * This cost a long debugging session: `preview()` returned the envelope, so
 * `data.audioUrl` was `undefined`, the player fetched the literal string
 * "undefined" (resolving to /settings/undefined on the app origin), and the
 * browser reported HTTP 404 / NotSupportedError — which looked exactly like a
 * corrupt audio file or a broken storage URL. Every server-side layer checked
 * out because the browser was never contacting storage at all.
 *
 * `getVoiceClones` already worked around this inline; this is that same fix,
 * shared, so the next method doesn't have to rediscover it.
 */
function unwrapPayload<T>(response: unknown): T | undefined {
  const outer = (response as ApiResponse<unknown>)?.data;
  if (
    outer &&
    typeof outer === "object" &&
    !Array.isArray(outer) &&
    "statusCode" in outer &&
    "data" in outer
  ) {
    return (outer as { data: T }).data;
  }
  return outer as T | undefined;
}

class VoiceCloneService {
  private handleError(error: unknown, operation: string): never {
    const normalized = normalizeApiError(error);
    const message = normalized.message || `Failed to ${operation}`;
    // silent = MFA-window 403 the interceptor self-recovers — never toast it
    if (!normalized.silent) { showToast(message, "error") }
    throw new Error(message);
  }

  async getVoiceClones(): Promise<VoiceClone[]> {
    try {
      const response = await api.get({
        url: getApiUrl(endpoint.voiceClone.list),
        auth: true,
      });
      const payload = unwrapPayload<VoiceClone[]>(response);
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      this.handleError(error, 'fetch voice clones');
    }
  }

  async uploadVoiceSample(file: File, name: string, description?: string, consentAcknowledged?: boolean): Promise<VoiceClone> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      if (description) {
        formData.append('description', description);
      }
      // Required by the backend: attestation that the voice is the uploader's
      // own or they have written permission (ElevenLabs ToS + FCC/CA AB 1248).
      formData.append('consentAcknowledged', consentAcknowledged ? 'true' : 'false');

      const response = await api.post({
        url: getApiUrl(endpoint.voiceClone.upload),
        auth: true,
        body: formData,
      });
      const payload = unwrapPayload<VoiceClone>(response);
      if (!payload?.id) {
        throw new Error('Voice clone response was malformed');
      }
      return payload;
    } catch (error) {
      this.handleError(error, 'upload voice sample');
    }
  }

  async deleteVoiceClone(id: string): Promise<void> {
    try {
      await api.delete({
        url: getApiUrl(endpoint.voiceClone.delete(id)),
        auth: true,
      });
    } catch (error) {
      this.handleError(error, 'delete voice clone');
    }
  }

  async setDefault(id: string): Promise<void> {
    try {
      await api.post({
        url: getApiUrl(endpoint.voiceClone.setDefault(id)),
        auth: true,
        body: {},
      });
    } catch (error) {
      this.handleError(error, 'set default voice clone');
    }
  }

  async preview(id: string, text: string): Promise<{ audioUrl: string }> {
    try {
      const response = await api.post({
        url: getApiUrl(endpoint.voiceClone.preview(id)),
        auth: true,
        body: { text },
      });
      const payload = unwrapPayload<{ audioUrl: string }>(response);
      // Guard on the FIELD, not just the object: the old check passed on the
      // inner envelope and let `undefined` through as an audio URL.
      if (!payload?.audioUrl) {
        throw new Error('Preview response did not include an audio URL');
      }
      return payload;
    } catch (error) {
      this.handleError(error, 'generate preview');
    }
  }

  async testRvm(id: string, text?: string): Promise<{ callSid: string }> {
    try {
      const response = await api.post({
        url: getApiUrl(endpoint.voiceClone.testRvm(id)),
        auth: true,
        body: { text },
      });
      const payload = unwrapPayload<{ callSid: string }>(response);
      if (!payload?.callSid) {
        throw new Error('Test voicemail response did not include a call id');
      }
      return payload;
    } catch (error) {
      this.handleError(error, 'send test voicemail');
    }
  }
}

export const voiceCloneService = new VoiceCloneService();
export default voiceCloneService;
